<template>
  <v-row justify="center">
    <v-col cols="9">
      <v-card>
        <v-toolbar color="transparent" flat height="55">
          <v-toolbar-title class="text--secondary subtitle-1 font-weight-medium">
            <router-link :to="{ name: 'AccountExports' }" class="subtitle-1 routerLink font-weight-medium">Exportar</router-link>
            <v-icon class="mx-1" color="secondary">mdi-chevron-right</v-icon>
            Descargas
          </v-toolbar-title>
          <v-spacer />
          <v-btn text color="primary" :loading="$store.state.exports.retrieveLoader" @click="getList" :ripple="false">
            <v-icon small left>mdi-refresh</v-icon>Actualizar
          </v-btn>
        </v-toolbar>

        <v-divider />

        <v-tabs height="40">
          <v-tab :to="{ name: 'AccountExportDownloads' }" exact>Todas</v-tab>
          <v-tab :to="{ name: 'AccountExportDownloads', query: { status: 'pending' } }" exact>Pendiente</v-tab>
          <v-tab :to="{ name: 'AccountExportDownloads', query: { status: 'processing' } }" exact>Procesando</v-tab>
          <v-tab :to="{ name: 'AccountExportDownloads', query: { status: 'finalized' } }" exact>Finalizada</v-tab>
          <v-tab :to="{ name: 'AccountExportDownloads', query: { status: 'failed' } }" exact>Fallida</v-tab>
        </v-tabs>

        <v-divider />

        <v-card-text class="px-0 py-0">
          <v-skeleton-loader v-if="$store.state.exports.retrieveLoader" transition="fade-transition" type="article" />
          <template v-else>
            <template v-if="list.length">
              <v-data-table :headers="headers" :items="list" :items-per-page="pagination" hide-default-header disable-sort mobile-breakpoint hide-default-footer>
                <template v-slot:header="{ props: { headers } }">
                  <VTableHeaders :headers="headers" @sort="setSort" />
                </template>
                <template v-slot:[`item.type`]="{item}">
                  <span class="d-block text--secondary font-weight-medium">{{typeLabel(item)}}</span>
                  <span class="d-block body-2 text--secondary">{{item.id}}</span>
                </template>
                <template v-slot:[`item.count`]="{item}">
                  <span>{{item.count}} registro{{item.count === 1 ? '' : 's'}}</span>
                </template>
                <template v-slot:[`item.created`]="{item}">
                  <span>{{item.created | dateTime}}</span>
                </template>
                <template v-slot:[`item.status`]="{item}">
                  <v-chip v-if="item.status === 'pending'" class="mx-1" color="defaultGrey" text-color="secondary" small>Pendiente</v-chip>
                  <v-chip v-else-if="item.status === 'processing'" class="mx-1" color="defaultGrey" text-color="secondary" small>Procesando</v-chip>
                  <v-chip v-else-if="item.status === 'finalized' && item.file_deleted" class="mx-1" color="defaultGrey" text-color="secondary" small>Expirada</v-chip>
                  <v-chip v-else-if="item.status === 'finalized'" class="mx-1" color="defaultGrey" text-color="secondary" small>Lista<v-icon class="ml-1" x-small>mdi-check</v-icon></v-chip>
                  <v-tooltip v-else bottom max-width="320">
                    <template v-slot:activator="{on}">
                      <v-chip class="mx-1" color="defaultGrey" text-color="secondary" small v-on="on">Fallida<v-icon class="ml-1" x-small>mdi-close</v-icon></v-chip>
                    </template>
                    <span>{{item.error || 'No pudimos generar el archivo.'}}</span>
                  </v-tooltip>
                </template>
                <template v-slot:[`item.actions`]="{item}">
                  <div class="text-right">
                    <v-btn v-if="item.download_available" :loading="downloading === item.id" text color="primary" @click="download(item)">
                      <v-icon small left>mdi-download</v-icon>Descargar
                    </v-btn>
                    <span v-else-if="item.file_deleted" class="body-2 text--disabled">
                      El archivo expiró
                    </span>
                  </div>
                </template>
              </v-data-table>
              <v-divider />
              <v-row class="mx-0" align="center">
                <v-col cols="6">
                  <span class="body-2 text--secondary">{{count}} registros</span>
                </v-col>
                <v-col cols="6" class="text-right">
                  <v-pagination v-model="page" :length="totalPages" total-visible="6" />
                </v-col>
              </v-row>
            </template>
            <v-row v-else>
              <v-col class="text-center py-5">
                <v-icon class="d-block" color="secondary" size="48">mdi-download</v-icon>
                <span class="d-block subtitle-1">No existen descargas</span>
                <span class="d-block body-2 text--secondary">Las exportaciones que solicites aparecerán acá.</span>
              </v-col>
            </v-row>
          </template>
        </v-card-text>
      </v-card>
    </v-col>
  </v-row>
</template>
<script>
import { mapState } from 'vuex'
import VTableHeaders from '@/components/VTable/VTableHeaders'
import ListViewMixin from '@/mixins/ListViewMixin'

const TYPE_LABELS = {
  purchase_data: 'Compras (datos)',
  purchase_xml: 'Compras (documentos XML)'
}

export default {
  components: {
    VTableHeaders
  },
  mixins: [
    ListViewMixin
  ],
  data: () => ({
    downloading: null,
    headers: [
      { text: 'Exportación', value: 'type', class: 'font-weight-bold' },
      { text: 'Registros', value: 'count' },
      { text: 'Usuario', value: 'user' },
      { text: 'Fecha', value: 'created', sortable: true },
      { text: 'Estado', value: 'status' },
      { text: '', value: 'actions', width: 200, align: 'end' }
    ]
  }),
  computed: mapState({
    count: state => state.exports.downloadsCount,
    list: state => state.exports.downloadsList
  }),
  methods: {
    typeLabel (item) {
      return TYPE_LABELS[item.type] || item.type
    },
    getList () {
      this.$store.dispatch('exports/LIST', {
        resource: 'exports/downloads',
        query: this.$route.query
      })
    },
    download (item) {
      /**
       * La API devuelve una URL prefirmada de corta duración generada en el
       * momento del clic. Nunca se persiste ni viaja por email.
       */
      this.downloading = item.id
      this.$store.dispatch('exports/RETRIEVE', {
        resource: 'exports/downloads',
        id: item.id,
        detail: 'download',
        loader: false,
        // El detalle es {url}, no el objeto: no debe pisar downloadsDetail.
        setDetail: false
      })
      .then((response) => {
        window.location.assign(response.data.url)
      })
      .catch(() => {
        this.$dialog.message.error('No pudimos generar el enlace de descarga. Es posible que el archivo haya expirado.')
      })
      .finally(() => {
        this.downloading = null
      })
    }
  }
}
</script>
